<template>
  <div class="production-generator">
    <canvas id="canvas" ref="canvasEl" :height="canvasResolution" :width="canvasResolution"></canvas>
  </div>
</template>

<script setup lang="ts">

import { ref, computed, onMounted } from 'vue'
import {
  Canvas as FabricCanvas,
  Group as FabricGroup,
  Rect as FabricRect,
  Pattern as FabricPattern,
  Gradient as FabricGradient,
  FabricImage,
  FabricObject,
  loadSVGFromURL,
  loadSVGFromString,
  config as fabricConfig,
} from 'fabric'


import * as opentypeNs from 'opentype.js'
import { showToast,showConfirm } from '@/composables/swal';
const opentypeLoad: (url: string, cb: (err: Error | null, font: any) => void) => void =
  (opentypeNs as any).load ?? (opentypeNs as any).default?.load


type AnyObj = FabricObject & Record<string, any>


function shallowClone<T>(obj: T): T {
  return Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj) as T

}


interface SvgGroupConfig {
  id: string
  color?: string
  gradient_colors?: { color: string }[]
}

interface PatternConfig {
  pattern_url: string
  random_string: string
  color?: { value: string }
  scale: string | number
  angle?: number
  name?: string
  url?: string
}

interface LogoConfig {
  url: string
  left: number
  top: number
  scaleX?: number
  scaleY?: number
  angle?: number
  is_customizable?: boolean
  placement_title?: string
}

interface CustomLogoConfig {
  url: string
  x_axis: number
  y_axis: number
  x_axis_3d?: number
  y_axis_3d?: number
  height: number
  rotation: number
  scaleX?: number
  scaleY?: number
}

interface TextItemConfig {
  x_axis: number
  y_axis: number
  x_axis_3d?: number
  y_axis_3d?: number
  height: number
  rotation: number
  color: string
  outline_color: string
  outline_width: string | number
  scaleX?: number
  scaleY?: number
}

interface CustomTextConfig {
  value: string
  font_url: string
  items: TextItemConfig[]
}


const props = withDefaults(
  defineProps<{
    designUrl: string
    sizesUrl: string
    safeZoneUrl: string
    canvasResolution?: number
    containerWidth?: number
    containerHeight?: number
    twoDCanvasWidth?: number
    twoDCanvasHeight?: number
    groupPatterns: Record<string, PatternConfig>
    logos?: LogoConfig[]
    custom_logos?: CustomLogoConfig[]
    texts?: CustomTextConfig[]
    svgGroups?: SvgGroupConfig[]
    sizeDownload: string
  }>(),
  {
    canvasResolution: 2048,
    containerWidth: 512,
    containerHeight: 512,
    twoDCanvasWidth: 600,
    twoDCanvasHeight: 600,
    logos: () => [],
    custom_logos: () => [],
    texts: () => [],
    svgGroups: () => [],
  }
)


const emit = defineEmits<{
  (e: 'resetDownloadProductionFile'): void
  (e: 'error', message: string): void
}>()


const canvasEl = ref<HTMLCanvasElement | null>(null)



let canvas: FabricCanvas
let design: FabricGroup

let size: FabricGroup
let mainSize: FabricGroup

let safeZone: FabricGroup
let mainSizeId = ''
let sizeSpec: Record<string, { width: number; height: number }> = {}
let mainSizeSpec: Record<string, { width: number; height: number }> = {}
let sizeDifferencesRatio: Record<string, { width: number; height: number }> = {}


const patterns: Record<string, FabricPattern | FabricRect> = {}

const storageUrl = `${(import.meta as any).env?.VITE_APP_FILE_URL ?? ''}/`

const canvasWidthRatio = computed(() => props.containerWidth / props.twoDCanvasWidth)
const canvasHeightRatio = computed(() => props.containerHeight / props.twoDCanvasHeight)

onMounted(() => { loadScene() })

async function loadScene(): Promise<void> {
  if (!canvasEl.value) return

  canvas = new FabricCanvas(canvasEl.value, { selection: false })

  const sizeFound = await addSize(props.sizeDownload, false)
  if (!sizeFound) {
    emit('resetDownloadProductionFile')
    showToast(`Size "${props.sizeDownload}" not found in the SVG`, `error`)
    return
  }

  calculateSizeDifferences()
  await addDesign()
  await downloadDesign()
}

async function downloadDesign(): Promise<void> {
  let targetWidth: number = (design as AnyObj)?.width ?? props.canvasResolution
  let targetHeight: number = (design as AnyObj)?.height ?? props.canvasResolution

  const anchor = design?.getObjects().find(
    (o: AnyObj) => typeof o.id === 'string' && o.id.toLowerCase().includes('anchor')
  ) as AnyObj | undefined

  if (anchor?.width && anchor?.height) {
    targetWidth = anchor.width as number
    targetHeight = anchor.height as number
  }

  const svg = generateProductionSVG(targetWidth, targetHeight)
  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `design_${props.sizeDownload}.svg`
  a.click()
  URL.revokeObjectURL(url)
  emit('resetDownloadProductionFile')
}


async function addDesign(): Promise<void> {
  const svgText = await fetch(storageUrl + props.designUrl).then(r => r.text())

  const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  const hiddenDiv = document.createElement('div')
  hiddenDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden'
  hiddenDiv.appendChild(svgDoc.documentElement)
  document.body.appendChild(hiddenDiv)

  try {
    const { objects } = await loadSVGFromURL(storageUrl + props.designUrl)
    const objs = (objects ?? []) as AnyObj[]

    changeColors(objs)

    const grouped = groupObjectsBySVGHierarchy(objs, hiddenDiv.children[0] as unknown as Document)

    const img = new FabricGroup(grouped, {
      hasControls: true,
      selectable: true,
      evented: true,
      lockMovementX: false,
      flipY: true,
    }) as AnyObj

    img.scaleToHeight(props.canvasResolution)
    img.set({ left: 0, top: 0 })
    img.setCoords()

    canvas.add(img)
    canvas.requestRenderAll()

    canvas.sendObjectToBack(img)

    design = img as FabricGroup

    applyAnchorDifferences(design)

    if (props.safeZoneUrl) await addSafeZone()
    await addFixedLogos()
    await addLogos()
    await addTexts()
    await applyAllPatterns()

    if (mainSizeId !== props.sizeDownload) reScaleLogosAndTexts()
  } finally {
    hiddenDiv.parentNode?.removeChild(hiddenDiv)
  }
}


async function addSize(size_param: string, isMainSize: boolean): Promise<boolean> {
  const svgText = await fetch(storageUrl + props.sizesUrl).then(r => r.text())
  const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml')

  let targetSizeId = size_param
  console.log(`Looking for size group "${targetSizeId}" in SVG...`, svgDoc)
  if (isMainSize) {
    const detected = findMainSizeId(svgDoc)
    if (detected) {
      targetSizeId = detected
      mainSizeId = detected
    } else {
      const firstGroup = svgDoc.querySelector('g[id]')
      console.log(firstGroup ? `No Anchor found; defaulting to first group "${firstGroup.getAttribute('id')}"` : 'No groups with IDs found in SVG')
      if (firstGroup) {
        targetSizeId = firstGroup.getAttribute('id') ?? ''
        mainSizeId = targetSizeId
      }
    }
  }

  if (!targetSizeId || !svgDoc.querySelector(`g[id="${targetSizeId}"]`)) {
    console.error(`Size group "${targetSizeId}" not found in SVG`)
    return false
  }


  svgDoc.querySelectorAll('g[id]').forEach(g => {
    if (g.getAttribute('id') !== targetSizeId) g.remove()
  })

  const filteredSvg = new XMLSerializer().serializeToString(svgDoc)


  const hiddenDiv = document.createElement('div')
  hiddenDiv.style.cssText = 'position:absolute;left:-9999px;visibility:hidden'
  hiddenDiv.appendChild(svgDoc.documentElement)
  document.body.appendChild(hiddenDiv)

  const spec: Record<string, { width: number; height: number }> = {}
  const groupEl = hiddenDiv.children[0]?.children[0]
  if (groupEl) {
    groupEl.childNodes.forEach(child => {
      const el = child as Element & { id?: string; getBBox?: () => DOMRect }
      if (el.nodeType === Node.ELEMENT_NODE && el.id) {
        const id = el.id.split('_')[0].toLowerCase()
        const bbox = el.getBBox?.()
        if (bbox) spec[id] = { width: bbox.width, height: bbox.height }
      }
    })
  }

  if (isMainSize) {
    mainSizeSpec = spec
  } else {
    sizeSpec = spec
  }

  hiddenDiv.parentNode?.removeChild(hiddenDiv)


  const { objects } = await loadSVGFromString(filteredSvg)


  const img = new FabricGroup((objects ?? []) as FabricObject[], {
    hasControls: false,
    selectable: false,
    evented: false,
    lockMovementX: true,
    lockMovementY: true,
    flipY: true,
  })
  img.scaleToHeight(props.canvasResolution)
  img.set({ left: 0, top: 0 })
  img.setCoords()


  // FIX-1: In Fabric v6, canvas.sendObjectToBack() is used instead of
  // the Group method which requires an object argument.
  canvas.add(img)
  canvas.sendObjectToBack(img)


  if (isMainSize) {
    mainSize = img
  } else {
    size = img
  }

  return true
}


function findSizeGroupByID(id: string): AnyObj | null {
  if (!size) return null

  const objs = (size as AnyObj)._objects as AnyObj[] | undefined
  if (!objs) return null

  const index = objs.findIndex((s: AnyObj) => {

    const normalised = String(s.id).includes('_')
      ? String(s.id).substring(0, String(s.id).indexOf('_'))
      : String(s.id)
    return normalised === id
  })

  return index !== -1 ? objs[index] : null
}

function findMainSizeId(svgDoc: Document): string | null {
  const anchor = svgDoc.getElementById('Anchor')
  if (!anchor) return null
  let parent: Element | null = anchor.parentElement
  while (parent && parent !== svgDoc.documentElement) {
    if (parent.tagName.toLowerCase() === 'g' && parent.getAttribute('id')) {
      return parent.getAttribute('id')
    }
    parent = parent.parentElement
  }
  return null
}

function calculateSizeDifferences(): void {
  const diff: typeof sizeDifferencesRatio = {}
  Object.keys(sizeSpec).forEach(key => {
    const bw = mainSizeSpec[key]?.width ?? 0
    const bh = mainSizeSpec[key]?.height ?? 0
    const tw = sizeSpec[key]?.width ?? 0
    const th = sizeSpec[key]?.height ?? 0
    diff[key] = { width: bw ? tw / bw : 1, height: bh ? th / bh : 1 }
  })
  sizeDifferencesRatio = diff
}


function changeColors(objects: AnyObj[]): void {
  if (!props.svgGroups?.length) return
  objects.forEach(item => {
    if (!item.id) return
    const itemId = String(item.id).split('_')[0].toLowerCase()
    const group = props.svgGroups!.find(g => g.id === itemId)
    if (!group) return

    const fill = item.fill as any
    if (fill?.gradientUnits) {
      const stops = fill.colorStops as { color: string }[]
      if (group.gradient_colors?.length) {
        group.gradient_colors.forEach((gc, i) => { if (stops[i]) stops[i].color = gc.color })
        item.set('fill', new FabricGradient(fill))
      } else if (group.color && stops[0]) {
        stops[0].color = group.color
        item.set('fill', new FabricGradient(fill))
      }
    } else {
      item.set('fill', group.gradient_colors?.length ? group.gradient_colors[0].color : group.color)
    }
  })
}


function groupObjectsBySVGHierarchy(objects: AnyObj[], svgDoc: Document): FabricObject[] {
  const groupMap = new Map<string, AnyObj[]>()

  objects.forEach(obj => {
    if (!obj.id) return
    const el = svgDoc.getElementById(String(obj.id))
    if (!el) return

    let parent = el.parentElement
    let highestGroup: Element | null = null
    while (parent && parent !== svgDoc.documentElement) {
      if (parent.tagName.toLowerCase() === 'g' && parent.id) highestGroup = parent
      parent = parent.parentElement
    }

    const gid = (highestGroup?.id ?? String(obj.id)).toLowerCase()
    if (!groupMap.has(gid)) groupMap.set(gid, [])
    groupMap.get(gid)!.push(obj)
  })

  const result: FabricObject[] = []
  groupMap.forEach((items, gid) => {
    const ratio = sizeDifferencesRatio[gid]
    let out: AnyObj

    if (items.length > 1) {
      out = new FabricGroup(items) as AnyObj
      out.id = gid
    } else {
      out = items[0]
    }

    if (ratio) scaleKeepAnchor(out, ratio.width, ratio.height, 'center', 'center')
    result.push(out)
  })

  return result
}


function scaleKeepAnchor(
  obj: AnyObj,
  newScaleX: number,
  newScaleY: number,

  anchorX: 'left' | 'center' | 'right' = (obj.originX as 'left' | 'center' | 'right') || 'left',
  anchorY: 'top' | 'center' | 'bottom' = (obj.originY as 'top' | 'center' | 'bottom') || 'top'

): void {
  if (obj.scaleX === newScaleX && obj.scaleY === newScaleY) return
  const anchor = obj.getPointByOrigin(anchorX, anchorY)
  obj.set({ scaleX: newScaleX, scaleY: newScaleY })
  obj.setPositionByOrigin(anchor, anchorX, anchorY)
  obj.setCoords()
  obj.canvas?.requestRenderAll()
}


function findMostLeftAndTopElements(d: FabricGroup): { maxLeft: number; maxTop: number } {
  const objs = (d as AnyObj)._objects as AnyObj[] | undefined
  if (!objs) return { maxLeft: 0, maxTop: 0 }

  let maxLeft = Infinity, maxTop = Infinity
  objs.forEach(o => {
    if ((o.left ?? 0) < maxLeft) maxLeft = o.left ?? 0
    if ((o.top ?? 0) < maxTop) maxTop = o.top ?? 0
  })
  return { maxLeft: isFinite(maxLeft) ? maxLeft : 0, maxTop: isFinite(maxTop) ? maxTop : 0 }
}

function applyAnchorDifferences(d: FabricGroup): void {
  const allObjs = (d as AnyObj)._objects as AnyObj[] | undefined
  if (!allObjs) return

  const anchors = allObjs.filter(
    o => typeof o.id === 'string' && o.id.toLowerCase().includes('anchor')
  )
  if (!anchors.length) return

  const anchorGroup = new FabricGroup(cloneFabricObjects(anchors), {
    hasControls: false, selectable: false, evented: false,
    lockMovementX: true, lockMovementY: true, flipY: true,
  })
  anchorGroup.scaleToHeight(props.canvasResolution)
  anchorGroup.set({ left: 0, top: 0 }).setCoords()

  ;(d as AnyObj).set({ scaleX: anchorGroup.scaleX, scaleY: anchorGroup.scaleY }).setCoords()

  const { maxLeft, maxTop } = findMostLeftAndTopElements(d)
  const al = anchors[0]?.left ?? 0
  const at = anchors[0]?.top ?? 0
  const sx = anchorGroup.scaleX ?? 1
  const sy = anchorGroup.scaleY ?? 1

  const diffX = (maxLeft < 0 && al < 0)
    ? Math.abs(maxLeft - al) * sx
    : Math.abs(al - maxLeft) * sx

  const diffY = (maxTop < 0 && at < 0)
    ? Math.abs(maxTop - at) * sy
    : Math.abs(at - maxTop) * sy

  const heightDiff = d.getScaledHeight() - props.canvasResolution
  ;(d as AnyObj).set({
    left: ((d as AnyObj).left ?? 0) - diffX,
    top: ((d as AnyObj).top ?? 0) - (heightDiff - diffY),
  }).setCoords()
}


async function addSafeZone(): Promise<void> {
  const { objects } = await loadSVGFromURL(storageUrl + props.safeZoneUrl)
  const clip = new FabricGroup((objects ?? []) as FabricObject[], {
    hasControls: false, selectable: false, evented: false,
    lockMovementX: true, lockMovementY: true,
    absolutePositioned: true, inverted: true, flipY: true,
  }) as AnyObj


  canvas.centerObject(clip)
  clip.scaleToHeight(props.canvasResolution)
  clip.set({ left: 0, top: 0 }).setCoords()
  safeZone = clip as FabricGroup
}


async function addFixedLogos(): Promise<void> {
  for (const logo of props.logos ?? []) await addSvgLogo(logo)
}

async function addSvgLogo(logo: LogoConfig): Promise<void> {
  const url = encodeURI((storageUrl + logo.url).trim()) + '?nocache=11'
  const { objects } = await loadSVGFromURL(url)
  const img = new FabricGroup((objects ?? []) as FabricObject[]) as AnyObj

  img.set({
    left: logo.left, top: logo.top,
    scaleX: logo.scaleX ?? 1, scaleY: logo.scaleY ?? 1,
    angle: logo.angle ?? 0,
    hasControls: false, selectable: false, evented: false,
    lockMovementX: true, lockMovementY: true,
    globalCompositeOperation: 'source-atop',
    originX: 'center', originY: 'center',
    flipX: true, type: 'logo',
  })

  if (logo.is_customizable) {
    const svgGroup = containsObject({ id: logo.placement_title ?? '' })
    if (svgGroup) {
      const logoObjs: AnyObj[] = (img as AnyObj)._objects ?? [img]
      logoObjs.forEach(o => o.set({ fill: svgGroup.color }))
    }
  }

  canvas.add(img)
  canvas.requestRenderAll()
}

function containsObject(obj: { id: string }): SvgGroupConfig | null {
  return props.svgGroups?.find(g => g.id === obj.id) ?? null
}


async function addLogos(): Promise<void> {
  for (const logo of props.custom_logos ?? []) {
    if (logo?.url) await addLogo(logo)
  }
}

async function addLogo(logo: CustomLogoConfig): Promise<void> {
  const url = encodeURI((storageUrl + logo.url).trim()) + '?nocache=11'
  const base64 = await convertImageURLToBase64(url)

  const img = await FabricImage.fromURL(base64, { crossOrigin: 'anonymous' }) as AnyObj

  const ar = ((img.width ?? 1) as number) / ((img.height ?? 1) as number)
  if (ar > 1) img.scaleToWidth(logo.height * canvasWidthRatio.value)
  else         img.scaleToHeight(logo.height * canvasHeightRatio.value)

  const rot = logo.rotation < 0
    ? oppositeAngle(360 - logo.rotation)
    : oppositeAngle(logo.rotation)

  img.set({
    left: logo.x_axis_3d ?? logo.x_axis,
    top: logo.y_axis_3d ?? logo.y_axis,
    angle: rot,
    hasControls: false, selectable: false, evented: false,
    lockMovementX: true, lockMovementY: true,
    globalCompositeOperation: 'source-atop',
    originX: 'center', originY: 'center',
    flipX: true, type: 'logo',
  })

  if (logo.scaleX && logo.scaleY) {
    img.scaleX = logo.scaleX * canvasWidthRatio.value
    img.scaleY = logo.scaleY * canvasHeightRatio.value
  }

  img.clipPath = safeZone
  canvas.add(img)
  canvas.requestRenderAll()
}


async function addTexts(): Promise<void> {
  for (const t of props.texts ?? []) if (t?.value) await addText(t)
}

async function addText(customText: CustomTextConfig): Promise<void> {
  const font = await loadFont(customText.font_url)
  if (!font) return

  const path = font.getPath(customText.value, 0, 0, 72, {
    features: { liga: true, rlig: true },
  })

  const textSvg =
    '<?xml version="1.0" encoding="utf-8"?>\n' +
    '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"' +
    ' version="1.1" xml:space="preserve">\n' +
    path.toSVG() + '\n</svg>'

  await Promise.all(customText.items.map(async item => {
    const { objects } = await loadSVGFromString(textSvg)
    const fabricText = new FabricGroup((objects ?? []) as FabricObject[]) as AnyObj
    fabricText.scaleToHeight(item.height)

    const rot = item.rotation < 0
      ? oppositeAngle(360 - item.rotation)
      : oppositeAngle(item.rotation)

    fabricText.set({
      left: item.x_axis_3d ?? item.x_axis,
      top: item.y_axis_3d ?? item.y_axis,
      angle: rot,
      hasControls: false, selectable: false, evented: false,
      lockMovementX: true, lockMovementY: true,
      globalCompositeOperation: 'source-atop',
      fill: item.color,
      stroke: item.outline_color,
      strokeWidth: parseInt(String(item.outline_width), 10),
      paintFirst: 'stroke',
      lockScalingFlip: true,
      originX: 'center', originY: 'center',
      flipX: true, type: 'text',
    })

    if (item.scaleX && item.scaleY) {
      fabricText.scaleX = item.scaleX * canvasWidthRatio.value
      fabricText.scaleY = item.scaleY * canvasHeightRatio.value
    }

    fabricText.clipPath = safeZone
    canvas.add(fabricText)
    canvas.requestRenderAll()
  }))
}


async function applyAllPatterns(): Promise<void> {
  if (!props.groupPatterns) return

  await Promise.all(Object.keys(props.groupPatterns).map(part => applyPattern(part, false)))
}


async function applyPattern(svgPart: string, re_stack = true): Promise<void> {
  const patternData = props.groupPatterns[svgPart]
  if (!patternData?.name || !patternData?.url) return


  let pattern = patterns[svgPart] instanceof FabricPattern
    ? patterns[svgPart] as FabricPattern
    : undefined

  if (!pattern) {
    const created = await setupPattern(svgPart, patternData, design)
    if (!created) return
    pattern = created

    patterns[svgPart] = pattern
  }

  const designObjs = ((design as AnyObj)._objects as AnyObj[]) ?? [design as AnyObj]

  designObjs.flat().forEach((dObj: AnyObj, index: number) => {
    dObj.set('id', String(dObj.id).split('_')[0])
    if (String(dObj.id).toLowerCase() !== svgPart) return

    const latestObjs = (design as AnyObj)._objects as AnyObj[]

    const clipGroup = new FabricGroup(cloneFabricObjects(latestObjs), {
      hasControls: false, selectable: false, evented: false,
      lockMovementX: true, lockMovementY: true,
      absolutePositioned: true, flipY: true,
    }) as AnyObj

    const patternId = `pattern_group_${svgPart}`
    const occCount = latestObjs.filter(o => String(o.id) === patternId).length
    const objNum = index + occCount


    const clipObjs = (clipGroup as AnyObj)._objects as AnyObj[]
    clipObjs
      .filter((_: AnyObj, i: number) => i !== objNum)
      .forEach((o: AnyObj) => (clipGroup as FabricGroup).remove(o))

    const clipTarget = ((clipGroup as AnyObj)._objects as AnyObj[])[0]
    clipTarget.clipPath = undefined
    clipTarget.originalMatrix = clipTarget.calcTransformMatrix(true)

    clipGroup.scaleToHeight(props.canvasResolution)
    clipGroup.set({ left: 0, top: 0 }).setCoords()


    const finalPattern = occCount > 0

      ? shallowClone(pattern! as unknown as object) as unknown as FabricPattern

      : pattern!

    // FIX-4: FabricPattern doesn't overlap with AnyObj; cast via unknown first.
    ;(finalPattern as unknown as AnyObj).id = `pattern_${svgPart}_${occCount}`


    const patternRect = new FabricRect({
      left: dObj.left, top: dObj.top,
      width: dObj.width, height: dObj.height,
      hasControls: false, selectable: false, evented: false,
      originX: dObj.originX || 'left', originY: dObj.originY || 'top',
      lockMovementX: true, lockMovementY: true,
      absolutePositioned: true,
      fill: finalPattern as any,
    }) as AnyObj
    patternRect.clipPath = clipGroup
    patternRect.id = patternId

    const withRect = cloneFabricObjects(latestObjs)
    withRect.splice(objNum + 1, 0, patternRect)
    regroupDesign(withRect)


    patterns[svgPart] = patternRect as FabricRect
  })

  canvas.requestRenderAll()
}

async function setupPattern(
  svgPart: string,
  patternData: PatternConfig,
  d: FabricGroup,
  BASE_DESIGN_WIDTH = 6000
): Promise<FabricPattern | null> {
  const { objects } = await loadSVGFromURL(
    `${storageUrl}${patternData.pattern_url}?q=${patternData.random_string}`
  )
  if (!objects?.length) return null

  const img = new FabricGroup((objects ?? []) as FabricObject[]) as AnyObj
  const partCfg = props.groupPatterns[svgPart]

  if (partCfg.color?.value) {
    const fill = partCfg.color.value
    if ((img as AnyObj)._objects) {
      img.getObjects().forEach(o => (o as AnyObj).set('fill', fill))
    } else {
      img.set('fill', fill)
    }
  }

  const scaleVal = parseInt(String(partCfg.scale), 10)
  img.scale(((d as AnyObj).width as number / BASE_DESIGN_WIDTH) * (scaleVal / 100))

  const sw = img.getScaledWidth()
  const sh = img.getScaledHeight()

  const svgEl = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${sw} ${sh}" width="${sw}" height="${sh}">${img.toSVG()}</svg>`,
    'image/svg+xml'
  ).documentElement

  const svgImage = Object.assign(new Image(), {
    crossOrigin: 'Anonymous',
    src: svgToDataURL(svgEl),
  })
  const pat = new FabricPattern({ source: svgImage, repeat: 'repeat' })

  const angle = partCfg?.angle ?? 0
  const rad = (angle * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const tx = (sw / 2) * (1 - cos) + (sh / 2) * sin
  const ty = (sh / 2) * (1 - cos) - (sw / 2) * sin

  pat.patternTransform = [cos, sin, -sin, cos, -tx, -ty] as any

  return pat
}


function reScaleLogosAndTexts(): void {
  canvas.getObjects().forEach((lt: AnyObj) => {
    if (lt.type !== 'logo' && lt.type !== 'text') return
    design.getObjects().some((dObj: AnyObj) => {
      if (objectsOverlap(lt, dObj) && sizeDifferencesRatio[String(dObj.id)]) {
        const r = sizeDifferencesRatio[String(dObj.id)]
        const scaleWidthDiff = (1 - r.width) * -1
        const scaleHeightDiff = (1 - r.height) * -1
        const delta = pickDifference(scaleWidthDiff, scaleHeightDiff)
        lt.scaleX = (lt.scaleX ?? 1) + delta / (1 / (lt.scaleX ?? 1))
        lt.scaleY = (lt.scaleY ?? 1) + delta / (1 / (lt.scaleY ?? 1))
        return true
      }
      return false
    })
  })
  canvas.requestRenderAll()
}


function regroupDesign(designObjects: FabricObject[]): void {
  const g = new FabricGroup(designObjects, {
    hasControls: false, selectable: false, evented: false,
    lockMovementX: true, lockMovementY: true, flipY: true,
  })
  g.scaleToHeight(props.canvasResolution)
  g.set({ left: 0, top: 0 }).setCoords()

  canvas.remove(design)
  canvas.add(g)

  // FIX-1 (also line 915): use canvas.sendObjectToBack() in Fabric v6
  canvas.sendObjectToBack(g)


  applyAnchorDifferences(g)
  design = g
}


function generateProductionSVG(targetWidth: number, targetHeight: number): string {
  const matrixMap = new Map<string, string>()

  design.getObjects().flat().forEach((dObj: AnyObj) => {
    if (String(dObj.id).includes('pattern_') && dObj.clipPath) {
      const clipObj = ((dObj.clipPath as AnyObj)._objects as AnyObj[])[0]
      const cur = (clipObj.calcTransformMatrix() as number[]).map(roundOff).join(' ')
      const orig = (clipObj.originalMatrix as number[]).map(roundOff).join(' ')
      matrixMap.set(cur, orig)
    }
  })

  fabricConfig.NUM_FRACTION_DIGITS = 6

  let svg = canvas.toSVG()
  svg = svg.replace(/^\s*(<\?xml[^>]*>\s*)?(<!DOCTYPE[^>]*>\s*)?/i, '')


  svg = /width="[^"]+"/i.test(svg)
    ? svg.replace(/(width=")([^"]+)(")/i, `$1${targetWidth}$3`)
    : svg.replace(/(<svg)(\s)/i, `$1 width="${targetWidth}"$2`)


  svg = /height="[^"]+"/i.test(svg)
    ? svg.replace(/(height=")([^"]+)(")/i, `$1${targetHeight}$3`)
    : svg.replace(/(<svg)(\s)/i, `$1 height="${targetHeight}"$2`)

  matrixMap.forEach((original, current) => {
    const safe = current.replace(/\./g, '\\.').replace(/-/g, '\\-')
    svg = svg.replace(new RegExp(`matrix\\(${safe}\\)`, 'g'), `matrix(${original})`)
  })


  svg = svg
    .replace(/(<svg[^>]*>)/i,
      `$1<g transform="scale(1,-1) translate(0, -${props.canvasResolution})">`)
    .replace(/<\/svg>/i, '</g></svg>')


  Object.keys(patterns).forEach(svgPart => {
    const angle = props.groupPatterns[svgPart]?.angle
    if (!angle) return
    const rx = new RegExp(
      `<pattern([^>]+id="SVGID_pattern_${svgPart}_\\d+"[^>]*)>`,
      'g'
    )
    svg = svg.replace(rx, (_m, attrs: string) =>
      /patternTransform\s*=/.test(attrs)
        ? `<pattern ${attrs.replace(/patternTransform="[^"]*"/, `patternTransform="rotate(${angle})"`)}>`
        : `<pattern ${attrs} patternTransform="rotate(${angle})">`
    )
  })

  return svg
}


function cloneFabricObjects(objects: FabricObject[]): FabricObject[] {
  return objects.map(o => shallowClone(o))
}

function objectsOverlap(a: FabricObject, b: FabricObject): boolean {
  const ar = a.getBoundingRect()
  const br = b.getBoundingRect()
  return !(
    ar.left > br.left + br.width  ||
    ar.left + ar.width < br.left  ||
    ar.top  > br.top  + br.height ||
    ar.top  + ar.height < br.top
  )
}

function pickDifference(a: number, b: number): number {
  if (a < 0 && b < 0) return Math.max(a, b)
  if (a > 0 && b > 0) return Math.min(a, b)
  return Math.abs(a) < Math.abs(b) ? a : b
}

function oppositeAngle(angle: number): number {
  return (180 - angle + 360) % 360
}

function roundOff(n: number): number {
  return Math.round(n * 1_000_000) / 1_000_000
}

function svgToDataURL(svgElement: Element): string {
  const str = new XMLSerializer().serializeToString(svgElement)
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(str)))}`
}

function convertImageURLToBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = Object.assign(new Image(), { crossOrigin: 'anonymous', src: url })
    img.onload = () => {
      const c = Object.assign(document.createElement('canvas'), {
        width: img.width,
        height: img.height,
      })
      c.getContext('2d')!.drawImage(img, 0, 0)
      resolve(c.toDataURL('image/png'))
    }
    img.onerror = reject
  })
}

function loadFont(url: string): Promise<any> {
  return new Promise(resolve => {
    opentypeLoad(storageUrl + url, (err, font) => resolve(err ? null : font))
  })
}


defineExpose({
  setupPattern,
  addSvgLogo,
  containsObject,
  loadFont,
  findMostLeftAndTopElements,
  applyAnchorDifferences,
  generateProductionSVG,
  svgToDataURL,

  findSizeGroupByID,
})
</script>

<style scoped lang="scss">
.production-generator {
  canvas {
    width: 1200px !important;
    height: 1200px !important;
  }
}
</style>
